'use client';

/**
 * **Add Contact on the attribute treatment** (VALE-632) — the buffered create
 * form behind the two surfaces that create a contact: the Contacts drawer's add
 * mode and `AddContactDialog`.
 *
 * It is the Contacts counterpart of the inventory add dialog
 * (`InventoryAttributeFormView`, VALE-98 / [INVDRAW-17]) and follows that
 * surface's contract exactly, because that is what
 * `components/attributes/CLAUDE.md` §0 row 2 prescribes for a create form whose
 * fields mirror an edit surface that already exists:
 *
 * · every row's `toPatch` still runs and still produces the same
 *   `Partial<Contact>` patch it produces in the drawer — this surface renders
 *   {@link ContactAttributeGrid} itself, so there is **one** contact field list
 *   (`contact-attribute-specs.ts`) and no add-specific duplicate;
 * · {@link applyContactPatch} merges that patch into **local state** instead of
 *   sending it;
 * · `Save` runs the shared {@link toContactCreatePayload} transform over the
 *   accumulated buffer and fires a single create.
 *
 * ## The gate this ticket was written to answer
 *
 * The open question (§0, "does Contacts add mode still need to be off the
 * treatment?") was whether the `Contact type` switch survives here at all.
 * Nothing stores a contact's type — `isOrganisationContact` derives it from
 * whether `organisationName` is set — and on the *edit* path the grid carries a
 * `pending` state machine to hold the reader's choice until the row that
 * completes it commits.
 *
 * **It survives untouched, and the buffer is why.** That state machine resolves
 * a pending choice by asking whether the record now agrees with it; on the edit
 * path the record is a refetch, and here it is this buffer. So choosing
 * `Organisation` re-branches the row list, `Organisation name` auto-opens,
 * typing it lands in the buffer, and the pending choice retires itself because
 * the buffer now derives to `organisation` on its own — the same derivation,
 * one round-trip cheaper. Switching *back* after naming an organisation is the
 * case that actually had to be proven: the completing name row carries
 * `organisationName: null` (`individualNamePatch`), and the merge below turns
 * that into absent, so the create payload can never carry both names.
 * `contact-attribute-create-form.test.tsx` is that proof.
 *
 * ## What this surface deliberately does not render
 *
 * Each of these is already absent from today's RHF add path, so dropping them
 * keeps the two identical rather than changing the surface:
 *
 * · **Identity variants** (`Other names` / `Other addresses`) and **ID upload** —
 *   both attach to a contact id the add flow has not minted yet, and the drawer
 *   already gates them on the edit path.
 * · **The PMS binding outcome footer** — reported per `(contact, matter)`. The
 *   participant-type **selector** does still render, because
 *   `CreateContactRequest` carries `pmsContactTypeId` and today's add form
 *   submits it; see `ContactPmsAttributeSectionOptions.addMode`.
 */

import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AttributeUnsavedStateProvider,
  type AttributeSurfaceControl
} from '@/components/attributes';
import { FormActions } from '@/components/inventory/inventory-form-card/shared/FormActions';
import { toast } from '@/lib/toast';
import { ContactAttributeGrid } from './contact-attribute-grid';
import { toContactCreatePayload } from './contact-create-payload';
import {
  deriveIsBankVerified,
  isOrganisationContact
} from '@/lib/services/contacts/utils';
import {
  CreateContactRequestSchema,
  type Contact
} from '@/lib/services/contacts/types';

/**
 * Merge one row's patch into the buffered record.
 *
 * **`null` means "clear", and a record that does not exist yet has nothing to
 * clear — so it lands as absent.** Same contract as the inventory add dialog's
 * `applyPatch`, and the reason a switch back to `Individual` cannot leave a
 * stale `organisationName` behind: the completing name row's patch carries
 * `organisationName: null`, and that is what erases it from the buffer.
 *
 * `??` rather than falsiness, so a legitimate `false` or `0` is kept — an
 * unticked `Deceased` is a value, not an absence.
 *
 * A contact patch is flat (no nested blob to merge the way `itemInfo` is), with
 * one exception: `address` and `bankAccount` arrive whole from their own
 * editors, so replacing them is correct.
 */
export function applyContactPatch(
  previous: Partial<Contact>,
  patch: Partial<Contact>
): Partial<Contact> {
  const next: Record<string, unknown> = { ...previous };
  for (const [key, value] of Object.entries(patch)) {
    next[key] = value ?? undefined;
  }

  /*
    **The one field the buffer has to derive, because the server normally does
    it** (VALE-632).

    `Verified` is the only row on this surface that reads one key and writes
    another: its draft comes from `contact.isBankVerified`, but its `toPatch`
    writes `bankAccount.isVerified` — and deliberately so, because
    `isBankVerified` is response-only and every consumer strips it (see
    `contact-attribute-specs.ts`'s `bankVerifiedFlag`, and LF-6359 for the
    save-that-changed-nothing this replaced).

    On the edit path the round-trip closes that loop: the backend re-derives
    the matter-scoped flag from the account it was just sent —
    `isBankVerified: bankAccount?.isVerified === true`
    (`backend/src/matters/contacts/contacts.service.ts`, on create at :348 and
    on update at :701) — and the refetch brings the new value back. **A buffer
    has no round-trip**, so without this the toggle wrote `isVerified` into the
    record, re-read `isBankVerified` (still unset), and rendered itself back to
    `No` — which reads as a control that does not respond to being clicked.

    Mirroring the backend's own rule rather than inventing one, and only when
    the patch actually carries a bank account, so an unrelated commit never
    disturbs the flag. **The rule itself lives in `deriveIsBankVerified`**
    (VALE-1247), because this buffer is no longer the only one that needs it —
    both AI-review surfaces hold one too, and a second copy of the rule is how
    they came to disagree with this one.
  */
  if (patch.bankAccount !== undefined) {
    next.isBankVerified = deriveIsBankVerified(patch.bankAccount);
  }

  return next as Partial<Contact>;
}

export interface ContactAttributeCreateFormProps {
  /** The record to seed the buffer with — an empty contact, or a prefill. */
  contact: Partial<Contact>;
  /** `isEstateAdminType(matter?.type)` — picks the role group's estate branch. */
  isEstate: boolean;
  /** `hasMatterId` — gates the per-matter Notes row and the role group. */
  hasMatterContext: boolean;
  /** An LPR on an enquiry — see `ContactAttributeSpecOptions.enquiryContact`. */
  enquiryContact?: boolean;

  /** Which matter, for the PMS participant-type catalogue. */
  matterId?: number;
  isSubmitting?: boolean;
  /** Creates the contact. Receives the validated create payload. */
  onSave: (contact: Partial<Contact>) => void | Promise<void>;
  onCancel: () => void;
  /**
   * Whether anything is in the buffer, reported up so the hosting surface can
   * guard a dismiss that would discard it — the create form is the harder case
   * the treatment's leave-guard rule exists for (§0), because its buffer holds
   * edits that have never been sent anywhere.
   */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Fires as a field opens and closes, so a hosting drawer can keep `Escape`
   * for the open editor instead of letting it dismiss the whole surface. The
   * create form needs this for the same reason the edit path does — converting
   * add mode onto the treatment is what gave it inline editors to lose.
   */
  onEditingChange?: (editing: boolean) => void;
  /** The contact-search dropdown the drawer renders above the record. */
  headerContent?: React.ReactNode;
  /**
   * When provided, the footer portals into this node instead of rendering
   * inline — for the drawer, whose scroll area would otherwise bury it. Mirrors
   * `ContactFormCard`'s prop of the same name.
   */
  actionsContainer?: HTMLElement | null;
  /**
   * Defaults to `FormActions`' own `Save Changes` — which is exactly what
   * `ContactFormCard` produced on both add surfaces, since it never passed one.
   * Kept identical deliberately: the button's wording is not this conversion's
   * to change.
   */
  saveLabel?: string;
}

export function ContactAttributeCreateForm({
  contact,
  isEstate,
  hasMatterContext,
  enquiryContact = false,
  matterId,
  isSubmitting = false,
  onSave,
  onCancel,
  onDirtyChange,
  onEditingChange,
  headerContent,
  actionsContainer,
  saveLabel = 'Save Changes'
}: ContactAttributeCreateFormProps) {
  /*
    A one-shot initialiser, like the inventory add dialog's own: re-seeding from
    props would throw away everything committed since the surface opened.
  */
  const [draft, setDraft] = useState<Partial<Contact>>(() => ({ ...contact }));
  /*
    **A live mirror of the buffer, because `Save` now writes to it and reads it
    back inside one gesture** (VALE-1274). `handleSave` flushes the open editor
    first, which merges that row through {@link handleCommit} — a `setDraft`
    whose result the same handler cannot see. The ref is what it reads instead.

    The same ref-first idiom as the three below, and for the same reason: an
    answer that has to be right *during* an event, not after the render it
    schedules.
  */
  const draftRef = useRef<Partial<Contact>>(draft);
  /** The surface's handle on whatever editor a grid inside has open. */
  const attributeControlRef = useRef<AttributeSurfaceControl | null>(null);

  /*
    **Has the reader put anything in the buffer?** Only ever goes false → true:
    a committed value stays committed, so this is what gates Save and what makes
    a dismiss destructive.
  */
  const [dirty, setDirty] = useState(false);
  /*
    **A type switch the reader started and has not completed** — the row list is
    showing the type they chose while the buffer still derives to the other one.
    Save is disabled for exactly as long as this is true, which is what keeps
    the payload unambiguous: `toContactCreatePayload` has to be told which
    branch to keep, and a pending switch is precisely the state where the
    buffer and the reader's choice disagree about the answer. The grid has
    already opened the field that completes it, so the way out is in front of
    them rather than hidden behind an error.
  */
  const [pendingSwitch, setPendingSwitch] = useState(false);
  /*
    **An editor is open holding something the reader typed** (VALE-1274). It
    arms `Save` alongside {@link dirty}, and it has to, because the two are the
    same reader in two states: a name typed into the composite `Name` popover is
    every bit as much *something to save* as one that has already committed.

    Leaving it out is what made the reported defect unrecoverable rather than
    merely awkward. The popover row commits when it is dismissed, `Save` is the
    natural next click, and a `disabled` button cannot be the thing that
    dismisses it — so the reader sat in front of a Save that would not arm, with
    their name on screen, and no gesture that would fix it.
  */
  const [hasOpenDraft, setHasOpenDraft] = useState(false);

  /*
    **The three reasons leaving would lose something, unioned and reported
    synchronously** — the same shape (and the same reasoning) as the drawer's
    own `syncEditDirty` on the edit path.

    Refs beside the state, not derived in an effect: an effect defers the
    parent's update by a render cycle, and a dismiss arriving inside that cycle
    would read the surface as clean and take the buffer with it. VALE-164
    measured exactly that window on the inventory dialog. Both writers here are
    event handlers, so a plain call batches with their own `setState`.

    Unlike {@link dirty}, the union genuinely goes back to false — closing an
    open editor, or completing a type switch, un-dirties the surface.
  */
  const bufferDirtyRef = useRef(false);
  const attributeUnsavedRef = useRef(false);
  const pendingSwitchRef = useRef(false);

  const syncDirty = useCallback(() => {
    onDirtyChange?.(
      bufferDirtyRef.current ||
        attributeUnsavedRef.current ||
        pendingSwitchRef.current
    );
  }, [onDirtyChange]);

  /**
   * One row's commit. Resolves — which `useAttributeCommit` reads as
   * *accepted* — because the value really has been recorded, in the buffer the
   * footer will send. Nothing can reject here: there is no request to fail.
   *
   * Dirty is reported from the commit itself rather than from an effect, so a
   * dismiss arriving in the same dispatch still sees it (VALE-164 measured that
   * window on the inventory dialog and lost a committed BSB to it).
   */
  const handleCommit = useCallback(
    async (patch: Partial<Contact>) => {
      /*
        Merged into the ref first and the state from it, rather than the other
        way round: `Save` flushes the open editor and then reads the buffer in
        the same handler, and a `setDraft` updater has not run by then.
      */
      const next = applyContactPatch(draftRef.current, patch);
      draftRef.current = next;
      setDraft(next);
      bufferDirtyRef.current = true;
      setDirty(true);
      syncDirty();
    },
    [syncDirty]
  );

  const handleUnsavedChange = useCallback(
    (unsaved: boolean) => {
      attributeUnsavedRef.current = unsaved;
      syncDirty();
    },
    [syncDirty]
  );

  const handlePendingSwitchChange = useCallback(
    (pending: boolean) => {
      pendingSwitchRef.current = pending;
      setPendingSwitch(pending);
      syncDirty();
    },
    [syncDirty]
  );

  const handleSave = useCallback(async () => {
    /*
      **Commit what is on screen before deciding what to send** (VALE-1274).
      A row the reader has typed into but not dismissed is theirs, and dropping
      it would file a record missing the field they were looking at.

      A refusal stops the submit: the row is now wearing its reason, and going
      on would either send an invalid buffer or replace that reason with a toast
      about a different field.
    */
    if (attributeControlRef.current?.flushOpenEditors() === false) return;

    /*
      Read off the buffer, not off a pending choice — Save is disabled while one
      is outstanding, so by here the two agree. The ref rather than the state,
      because the flush above may have just merged a row into it.
    */
    const buffer = draftRef.current;
    const contactType = isOrganisationContact(buffer)
      ? 'organisation'
      : 'individual';
    const payload = toContactCreatePayload(buffer, contactType);

    const result = CreateContactRequestSchema.safeParse(payload);
    if (!result.success) {
      const errors = result.error.flatten();
      /*
        The treatment has no per-row error slot to write a field error into the
        way RHF's `setError` does, so a field-level failure is surfaced as the
        message it is. Both lists are shown: a create that fails on
        `submitsBothNames` reports through `formErrors`, and a bad email through
        `fieldErrors`, and a reader who sees neither would just watch Save do
        nothing.
      */
      const messages = [
        ...errors.formErrors,
        ...Object.entries(errors.fieldErrors).flatMap(([field, msgs]) =>
          msgs && msgs.length > 0 ? [`${field}: ${msgs[0]}`] : []
        )
      ];
      toast.error(
        messages.length > 0
          ? messages.join('\n')
          : 'This contact is missing something required.'
      );
      return;
    }

    await onSave(result.data as Partial<Contact>);
  }, [onSave]);

  const actions = (
    <FormActions
      onCancel={onCancel}
      onSave={handleSave}
      hasChanges={dirty}
      /*
        **Disabled while a type switch is outstanding**, on top of the usual
        "nothing entered yet". The row list is showing the type the reader
        chose while the buffer still derives to the other one, so there is no
        honest answer to *which branch does this record belong to* until the
        completing field is filled — and the grid has already opened it. Saving
        through that state is the one way this surface could file a record the
        reader did not describe.

        **Both refusals yield to an open editor the reader has typed into**
        (VALE-1274), and the pending-switch one has to: the field that completes
        the switch is the field the grid just opened, so *"wait until it is
        filled"* and *"you may not press Save"* together mean the reader who has
        filled it has no way to say so. `handleSave` commits it before it
        decides, so by the time the payload is built the answer is in the buffer
        — and a half-filled one is refused by the row's own `validate`, on the
        row, which says more than a disabled button ever did.
      */
      disabled={!hasOpenDraft && (!dirty || pendingSwitch)}
      isSubmitting={isSubmitting}
      saveLabel={saveLabel}
    />
  );

  return (
    <AttributeUnsavedStateProvider
      onUnsavedChange={handleUnsavedChange}
      onUncommittedDraftChange={setHasOpenDraft}
      controlRef={attributeControlRef}
    >
      {headerContent}
      <ContactAttributeGrid
        contact={draft as Contact}
        isEstate={isEstate}
        hasMatterContext={hasMatterContext}
        enquiryContact={enquiryContact}
        matterId={matterId}
        addMode
        onCommit={handleCommit}
        onEditingChange={onEditingChange}
        onPendingSwitchChange={handlePendingSwitchChange}
      />
      {actionsContainer ? (
        /*
          The drawer supplies its own footer well (`SingleItemEditDrawer`'s
          `onFooterContainerChange`); this is the exact chrome `ContactFormCard`
          portalled into it, so a converted add footer measures the same as the
          one it replaces.
        */
        createPortal(
          <div className="shrink-0 border-t px-6 py-4">{actions}</div>,
          actionsContainer
        )
      ) : (
        /*
          Inline, for a host with no footer well. `sticky bottom-0` keeps Save on
          screen on a record that runs past the fold, and `bg-background` is
          load-bearing rather than decoration — without an opaque fill the rows
          scroll visibly through it (VALE-285, on the inventory add dialog).
        */
        <div className="bg-background sticky bottom-0 mt-6 shrink-0 border-t pt-4 pb-2">
          {actions}
        </div>
      )}
    </AttributeUnsavedStateProvider>
  );
}
